import React, { useState } from "react";
import { Bot, AlertTriangle, TrendingUp, Target, Lightbulb, ArrowRight, X, Minimize2, Maximize2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilterState } from "@/pages/Reports";

interface AIInsightsPanelProps {
  filters: FilterState;
  onMinimize?: () => void;
  onClose?: () => void;
  isMinimized?: boolean;
}
// Sem insights inventados: as análises serão geradas a partir dos dados reais
// das obras (integração com o assistente Obra IA). Painel exibe estado vazio.
interface AIInsight {
  id: string;
  type: string;
  title: string;
  description: string;
  priority: string;
  action: string;
  impact: string;
  icon: typeof AlertTriangle;
  color: string;
  bgColor: string;
  borderColor: string;
}
const mockInsights: AIInsight[] = [];
const getPriorityBadge = (priority: string) => {
  switch (priority) {
    case 'high':
      return <Badge variant="destructive">Alta Prioridade</Badge>;
    case 'medium':
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Média Prioridade</Badge>;
    case 'low':
      return <Badge variant="outline">Baixa Prioridade</Badge>;
    default:
      return <Badge variant="outline">Indefinido</Badge>;
  }
};
export const AIInsightsPanel = ({
  filters,
  onMinimize,
  onClose,
  isMinimized = false
}: AIInsightsPanelProps) => {
  const [internalMinimized, setInternalMinimized] = useState(false);

  const minimized = isMinimized || internalMinimized;

  const handleMinimize = () => {
    if (onMinimize) {
      onMinimize();
    } else {
      setInternalMinimized(!internalMinimized);
    }
  };
  return <Card className={`${minimized ? 'p-4' : 'p-6'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-3 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 shrink-0">
            <Bot className="h-6 w-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-semibold">Inteligência com IA</h2>
            {!minimized && (
              <p className="text-muted-foreground">Insights e recomendações baseadas em dados</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!minimized && (
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              {mockInsights.length} insight(s) detectado(s)
            </Badge>
          )}
          <button
            onClick={handleMinimize}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            {minimized ? (
              <Maximize2 className="h-4 w-4 text-gray-500" />
            ) : (
              <Minimize2 className="h-4 w-4 text-gray-500" />
            )}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {!minimized && mockInsights.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground gap-3">
          <Bot className="h-10 w-10 opacity-40" />
          <div>
            <p className="font-medium text-foreground">Sem insights ainda</p>
            <p className="text-sm max-w-xl">
              As análises inteligentes serão geradas a partir dos RDOs e tarefas
              reais das suas obras. Use o assistente Obra IA para análises sob
              demanda.
            </p>
          </div>
        </div>
      )}
      {!minimized && mockInsights.length > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {mockInsights.map(insight => {
            const Icon = insight.icon;
            return <Card key={insight.id} className={`p-6 hover:shadow-lg transition-all duration-300 cursor-pointer ${insight.bgColor} ${insight.borderColor} border-l-4`}>
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-white shadow-sm">
                          <Icon className={`h-5 w-5 ${insight.color}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground leading-tight">
                            {insight.title}
                          </h3>
                          <div className="mt-2">
                            {getPriorityBadge(insight.priority)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {insight.description}
                    </p>

                    {/* Impact & Action */}
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-white/50">
                        <div className="text-xs font-medium text-muted-foreground mb-1">
                          IMPACTO ESPERADO
                        </div>
                        <div className="text-sm font-medium text-foreground">
                          {insight.impact}
                        </div>
                      </div>

                      <Button variant="outline" size="sm" className="w-full justify-between bg-white/80 hover:bg-white">
                        <span>{insight.action}</span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>;
          })}
          </div>

          {/* AI Performance Summary */}
          <div className="mt-8 p-6 rounded-lg bg-gradient-to-r from-primary/5 via-primary/3 to-primary/5 border border-primary/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Bot className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Performance da IA</h3>
                  <p className="text-sm text-muted-foreground">Últimos 30 dias</p>
                </div>
              </div>
              
            </div>
            
            <div className="grid grid-cols-3 gap-6 mt-4 pt-4 border-t border-primary/20">
              <div className="text-center">
                <div className="text-lg font-bold text-foreground">156</div>
                <div className="text-xs text-muted-foreground">Sugestões geradas</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">94</div>
                <div className="text-xs text-muted-foreground">Implementadas</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">60%</div>
                <div className="text-xs text-muted-foreground">Taxa de adoção</div>
              </div>
            </div>
          </div>
        </>
      )}
    </Card>;
};